import React, { useState, useRef, useEffect, DragEvent } from "react";
import { Download, File as FileIcon, X } from "lucide-react";
import "./style.scss";
import BaseLayout from "../../components/Layout";
import Button from "../../components/Button";
import { AppDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  postArquivoParaApi,
  resetUploadState,
} from "../../features/documentacaoAVCB/slices";
import { toast } from "sonner";
import {
  getDocumentacaoAVCBDaApi,
  resetGetDocumentacaoAVCBState,
} from "../../features/documentacaoAVCB/get-documentacao/slices";
import { getUser } from "../../helpers/user";
import {
  deleteDocumentacaoAVCBById,
  resetDeleteDocumentacaoAVCBState,
} from "../../features/documentacaoAVCB/delete-documentacao/slices";
import Select from "../../components/Select";
import { useAppSelector } from "../../hooks/useAppSelector";

const PageDocumentacaoAVCB = () => {
  const user = getUser();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [optionSelected, setOptionSelected] = useState("AVCB");
  const dispatch = useDispatch<AppDispatch>();
  const [dataVencimento, setDataVencimento] = useState<string>(() => {
    const now = new Date();
    const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    return localISO;
  });

  const minDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

  const { status } = useSelector((state: RootState) => state.upload);
  const { responseData: responseDataGetDocumentacao } = useSelector(
    (state: RootState) => state.getDocumentacaoAVCB,
  );

  const lojaSelected = useAppSelector((state) => state.getLojas.lojaSelected);
  const lojas = useAppSelector((state) => state.getLojas.lojas);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(
        getDocumentacaoAVCBDaApi({
          idLoja: user.usuario?.idLoja,
          idRegional: user?.usuario?.loja?.idRegional,
        }),
      );
    };

    fetchData();
  }, [dispatch, user.usuario?.idLoja, user?.usuario?.loja?.idRegional]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleFile = (file: File | null) => {
    if (!file) return;

    const acceptedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (acceptedTypes.includes(file.type)) {
      setSelectedFile(file);
    } else {
      alert(
        "Tipo de arquivo não suportado. Por favor, escolha JPG, PNG, PDF ou DOC/DOCX.",
      );
      setSelectedFile(null);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    } else {
      handleFile(null);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderPreview = () => {
    if (!selectedFile || !preview) return null;

    if (selectedFile.type.startsWith("image/")) {
      return (
        <img
          src={preview}
          alt="Pré-visualização da Imagem"
          className={"imagePreview"}
        />
      );
    }

    if (selectedFile.type === "application/pdf") {
      return (
        <object
          data={preview}
          type="application/pdf"
          width="100%"
          height="100%"
          className={"pdfPreview"}
        >
          <p>
            Seu navegador não tem um plugin para PDF. Você pode{" "}
            <a href={preview}>baixar o PDF para visualizá-lo.</a>
          </p>
        </object>
      );
    }

    return (
      <div className={"genericPreview"}>
        <FileIcon size={64} />
        <p>Arquivo selecionado:</p>
        <strong>{selectedFile.name}</strong>
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Por favor, selecione um arquivo antes de enviar.");
      return;
    }

    if (!user) {
      toast.error("Usuário não autenticado.");
      throw new Error("Usuário não autenticado.");
    }

    if (
      !dataVencimento ||
      new Date(dataVencimento).getTime() < new Date().getTime()
    ) {
      toast.error("Data de vencimento inválida.");
      return;
    }

    const formData = new FormData();

    formData.append("arquivoAVCB", selectedFile);
    formData.append("tipo_arquivo", optionSelected);
    formData.append("idUsuario", String(user?.usuario?.id));
    formData.append("idLoja", String(user?.usuario?.idLoja));
    formData.append("idRegional", String(user?.usuario?.loja?.idRegional));

    formData.append("enviador", String(user?.usuario?.login));
    formData.append("data_vencimento", String(dataVencimento));

    try {
      await dispatch(postArquivoParaApi(formData)).unwrap();

      dispatch(resetGetDocumentacaoAVCBState());

      await dispatch(
        getDocumentacaoAVCBDaApi({
          idLoja: user.usuario?.idLoja,
          idRegional: user?.usuario?.loja?.idRegional,
        }),
      ).unwrap();

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      dispatch(resetUploadState());
      dispatch(resetDeleteDocumentacaoAVCBState());

      setSelectedFile(null);
      setPreview(null);
    } catch (error: any) {
      console.error("Erro ao enviar o arquivo:", error);
      toast.error("Erro ao enviar o arquivo.");
    }
  };

  const handleDownload = async (fileName) => {
    const token = getUser()?.token;

    if (!token) {
      alert("Usuário não autenticado. Por favor, faça o login novamente.");
      return;
    }

    const url = `${process.env.REACT_APP_API}/documentacaoAVCB/download/${fileName}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            "Sessão expirada ou inválida. Por favor, faça o login.",
          );
        }
        throw new Error("Não foi possível baixar o arquivo.");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: Error | any) {
      console.error("Erro ao baixar o arquivo:", error);
      alert(error.message || "Ocorreu um erro.");
    }
  };

  const handleDelete = async (doc) => {
    if (
      !window.confirm("Você tem certeza que deseja deletar este documento?")
    ) {
      return;
    }

    try {
      await dispatch(deleteDocumentacaoAVCBById(doc.id));
      await dispatch(
        getDocumentacaoAVCBDaApi({
          idLoja: user.usuario?.idLoja,
          idRegional: user?.usuario?.loja?.idRegional,
        }),
      ).unwrap();
    } catch (error: Error | any) {
      toast.error(error.message || "Erro ao deletar o documento.");
    }
  };

  const options = [
    { value: "AVCB", children: "AVCB" },
    { value: "Laudo de Segurança", children: "Laudo de Segurança" },
    { value: "Ensaio de Bombas", children: "Ensaio de Bombas" },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOptionSelected(e.target.value);
  };

  return (
    <BaseLayout>
      <div className="container">
        <>
          {lojas.length > 0 && (
            <div className="container" style={{ marginBottom: "1.5rem" }}>
              <strong style={{ fontSize: "2.4rem", fontWeight: 700 }}>
                {lojas.find((loja) => loja.id === Number(lojaSelected))?.nome}
              </strong>
            </div>
          )}
        </>
        <h1 className={"mb-20"}>Documentação {optionSelected}</h1>
        <p className={"mb-20"}>
          Aqui você pode enviar/encontrar documentos relacionados ao{" "}
          {optionSelected}. Aceitamos arquivos nos formatos PDF, DOC, DOCX, JPG
          e PNG.
        </p>
        {[4, 5, 6, 999].includes(user.usuario.nivel) && (
          <form
            onSubmit={handleSubmit}
            method="post"
            className={"formContainer"}
          >
            <label htmlFor="fileInput" className={"fileInputLabel"}>
              Selecione ou arraste um arquivo
            </label>
            <div className={"uploaderContainer"}>
              <input
                type="file"
                title={""}
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/jpeg, image/png, application/pdf, .doc, .docx"
                className={"hiddenInput"}
              />

              <Select
                options={options}
                size="sm"
                value={optionSelected}
                onChange={handleChange}
              />

              <div className="input-group">
                <label htmlFor="data-vencimento">Data de Vencimento</label>
                <input
                  type="datetime-local"
                  id="data-vencimento"
                  name="data"
                  value={dataVencimento}
                  onChange={(e) => setDataVencimento(e.target.value)}
                  min={minDate}
                  className="datetime-input"
                />
              </div>

              {!selectedFile ? (
                <div
                  className={`dropZone ${isDragging ? "isDragging" : ""}`}
                  onClick={triggerFileSelect}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <FileIcon className={"icon"} size={48} />
                  <p className={"promptText"}>Arraste e Solte</p>
                  <p className={"fileHint"}>
                    Suporte para PDF, DOC, DOCX, JPG e PNG
                  </p>
                  <button type="button" className={"uploadButton"}>
                    Escolher Arquivo
                  </button>
                </div>
              ) : (
                <div>
                  <div className={"previewContainer"}>
                    {renderPreview()}

                    <button
                      title={"Remover Arquivo"}
                      onClick={handleRemoveImage}
                      className={"removeButton"}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <p className={"fileInfo"}>{selectedFile.name}</p>
                </div>
              )}
            </div>
            <div className="mt-20 buttonContainer">
              <Button size="sm" loading={status === "loading"}>
                {status === "loading" ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </form>
        )}
      </div>
      {responseDataGetDocumentacao &&
        responseDataGetDocumentacao.length > 0 && (
          <div className="documentTableContainer">
            <h2 className="mt-40 mb-20">Documentos Enviados</h2>
            <table className="documentTable">
              <thead>
                <tr>
                  <th>Nome do Arquivo</th>
                  <th>Data de Envio</th>
                  <th>Data de Vencimento</th>
                  <th>Tipo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {responseDataGetDocumentacao.map((doc) => (
                  <tr key={doc.id}>
                    <td
                      title={doc.nomeArquivoOriginal}
                      className="truncate-single-line"
                    >
                      {doc.nomeArquivoOriginal}
                    </td>
                    <td>
                      {new Date(doc.data_Inserido)
                        .toLocaleDateString("pt-br", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                        .replace(",", " às")}
                    </td>
                    <td>
                      {new Date(doc.data_Vencimento)
                        .toLocaleDateString("pt-br", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                        .replace(",", " às")}
                    </td>
                    <td>{doc.tipoArquivo}</td>
                    <td>
                      <div className="actionButtons">
                        <button
                          type="button"
                          title={""}
                          onClick={() => handleDownload(doc.nomeArquivoUnico)}
                          className="button-download"
                        >
                          <Download size={18} />
                        </button>
                        {[4, 5, 6, 999].includes(user.usuario.nivel) && (
                          <button
                            type="button"
                            title={"Remover Arquivo"}
                            onClick={() => handleDelete(doc)}
                            className="button-remove"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </BaseLayout>
  );
};

export default PageDocumentacaoAVCB;
